import { ArrowRight, Check, CheckCircle2, ChevronDown, Laptop, PartyPopper, CakeSlice, Briefcase, Wallet, Lightbulb, Languages, PlayCircle } from "lucide-react";
import { fill } from "./landingCopy";

export const Btn = ({ onClick, children, testid, variant = "green", full }) => (
  <button onClick={onClick} className={`min-h-[56px] px-7 rounded-2xl font-extrabold text-[15px] sm:text-[16px] inline-flex items-center justify-center gap-2 ${full ? "w-full" : "w-full sm:w-auto"} ${variant === "green" ? "bg-[#13B698] text-white" : variant === "navy" ? "bg-[#13283A] text-white" : "border-2 border-[#13283A] text-[#13283A] bg-white"}`} data-testid={testid}>{children} <ArrowRight className="h-5 w-5 shrink-0" /></button>
);
const Wrap = ({ children, className = "", testid }) => <section className={`py-10 sm:py-14 ${className}`} data-testid={testid}><div className="max-w-5xl mx-auto px-5">{children}</div></section>;
const H2 = ({ children, light }) => <h2 className={`font-display font-extrabold text-[24px] sm:text-4xl leading-tight ${light ? "text-white" : "text-[#13283A]"}`}>{children}</h2>;

export function Hero({ t, cta, loggedIn }) {
  const h = t.hero;
  return (
    <section className="bg-[#13283A] text-white" data-testid="challenge-hero">
      <div className="max-w-5xl mx-auto px-5 pt-7 pb-9 sm:py-16">
        <span className="inline-block rounded-full bg-[#13B698] text-[#13283A] text-[11px] font-extrabold px-3 py-1 tracking-wide" data-testid="challenge-eyebrow">{h.eyebrow}</span>
        <h1 className="font-display font-extrabold text-[27px] sm:text-5xl leading-[1.12] mt-4 max-w-3xl" data-testid="challenge-headline">{h.title}</h1>
        <p className="text-[16px] sm:text-lg text-white font-semibold mt-3 max-w-2xl">{h.sub1}</p>
        <p className="text-[15px] sm:text-lg text-white/80 mt-1.5 max-w-2xl">{h.sub2}</p>
        <div className="mt-5"><Btn onClick={() => cta("hero")} testid="challenge-hero-cta">{loggedIn ? h.ctaLoggedIn : h.cta}</Btn></div>
        <p className="text-[12px] sm:text-[13px] text-[#7FE3CF] font-semibold mt-2.5" data-testid="challenge-hero-note">{h.note}</p>
        <ul className="mt-5 grid gap-2 sm:grid-cols-3">{h.points.map((p) => <li key={p} className="flex gap-2 text-[14px] text-white/90"><Check className="h-5 w-5 text-[#13B698] shrink-0" strokeWidth={3} />{p}</li>)}</ul>
      </div>
    </section>
  );
}

export function Product({ t, ideas, cta }) {
  const p = t.product;
  return (
    <Wrap testid="challenge-product">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <H2>{p.title}</H2>
          <p className="text-[16px] text-[#36495A] mt-3">{p.text}</p>
          <div className="mt-6 hidden md:block"><Btn onClick={() => cta("product")} testid="challenge-product-cta">{p.cta}</Btn></div>
        </div>
        <div className="rounded-3xl bg-[#F4F8F9] border border-[#E1ECEF] p-4 space-y-3" data-testid="challenge-product-preview">
          <span className="inline-block rounded-full bg-[#13283A] text-white text-[10px] font-extrabold px-2.5 py-1">{p.example}</span>
          <div className="rounded-2xl bg-white p-4">
            <div className="text-sm font-bold text-[#13283A]">{p.question}</div>
            <div className="mt-2 grid grid-cols-2 gap-2">{p.options.map((o, i) => <span key={o} className={`rounded-xl border-2 px-3 py-2 text-xs font-semibold ${i === 2 ? "border-[#13B698] bg-[#E8F8F4]" : "border-[#E1ECEF]"}`}>{o}</span>)}</div>
          </div>
          <div className="text-xs font-bold text-[#5C6B73]">{p.recsTitle}</div>
          {ideas.slice(0, 3).map((i) => <div key={i.number} className="rounded-2xl bg-white p-3 flex items-center gap-3" data-testid={`challenge-preview-idea-${i.number}`}><Lightbulb className="h-5 w-5 text-[#13B698] shrink-0" /><div className="min-w-0"><div className="font-display font-bold text-[#13283A] text-sm leading-tight">{i.name}</div><div className="text-xs text-[#5C6B73]">{p.startFrom} {i.startup_cost}</div></div></div>)}
        </div>
        <div className="md:hidden"><Btn onClick={() => cta("product")} testid="challenge-product-cta-mobile">{p.cta}</Btn></div>
      </div>
    </Wrap>
  );
}

export function Problem({ t }) {
  const p = t.problem;
  return (
    <Wrap className="bg-[#F4F8F9]" testid="challenge-problem">
      <div className="max-w-3xl">
        <H2>{p.title}</H2>
        <div className="mt-4 space-y-2.5 text-[16px] text-[#36495A]">{p.lines.map((l) => <p key={l}>{l}</p>)}</div>
        <p className="mt-6 rounded-2xl bg-[#13283A] text-white p-5 font-display font-bold text-lg leading-snug" data-testid="challenge-problem-highlight">{p.highlight}</p>
      </div>
    </Wrap>
  );
}

export function Examples({ t, n, cta }) {
  const e = t.examples;
  const icons = [Laptop, PartyPopper, CakeSlice];
  return (
    <Wrap testid="challenge-examples">
      <H2>{e.title}</H2>
      <div className="grid sm:grid-cols-3 gap-3 mt-6">
        {e.cards.map(([title, d], i) => { const Icon = icons[i]; return (
          <div key={title} className="rounded-2xl border border-[#E1ECEF] p-5" data-testid={`challenge-example-${i}`}>
            <span className="h-12 w-12 rounded-2xl bg-[#E8F8F4] flex items-center justify-center"><Icon className="h-6 w-6 text-[#13B698]" /></span>
            <div className="font-display font-bold text-lg text-[#13283A] mt-3 leading-snug">{title}</div>
            <p className="text-[15px] text-[#5C6B73] mt-1">{d}</p>
          </div>
        ); })}
      </div>
      <p className="mt-5 text-[15px] font-semibold text-[#13283A]" data-testid="challenge-examples-more">{fill(e.more, { n })}</p>
      <div className="mt-5"><Btn onClick={() => cta("examples")} testid="challenge-examples-cta" variant="navy">{e.cta}</Btn></div>
    </Wrap>
  );
}

export function Days({ t, cta }) {
  const d = t.days;
  return (
    <Wrap className="bg-[#13283A]" testid="challenge-steps">
      <H2 light>{d.title}</H2>
      <p className="text-[16px] text-[#7FE3CF] font-semibold mt-2">{d.sub}</p>
      <ol className="mt-6 grid gap-3 sm:grid-cols-5">
        {d.items.map(([title, text], i) => (
          <li key={title} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`challenge-step-${i + 1}`}>
            <div className="flex sm:block items-center gap-3"><span className="h-9 w-9 rounded-full bg-[#13B698] text-[#13283A] font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
              <div className="sm:mt-3"><div className="text-[11px] font-extrabold text-[#13B698]">{d.day} {i + 1}</div><div className="font-display font-bold text-white leading-snug">{title}</div></div></div>
            <p className="text-[14px] text-white/75 mt-2">{text}</p>
          </li>
        ))}
      </ol>
      <p className="mt-4 flex items-center gap-2 text-[13px] text-white/70" data-testid="challenge-language-note"><Languages className="h-4 w-4 shrink-0" />{d.langNote}</p>
      <div className="mt-6"><Btn onClick={() => cta("days")} testid="challenge-days-cta">{d.cta}</Btn></div>
    </Wrap>
  );
}

export function Trust({ t }) {
  const icons = [Briefcase, Wallet, Lightbulb];
  return (
    <Wrap testid="challenge-trust">
      <H2>{t.trust.title}</H2>
      <div className="grid sm:grid-cols-3 gap-3 mt-6">{t.trust.cards.map(([title, d], i) => { const Icon = icons[i]; return <div key={title} className="rounded-2xl bg-[#F4F8F9] p-5" data-testid={`challenge-trust-${i}`}><Icon className="h-6 w-6 text-[#13B698]" /><div className="font-display font-bold text-[#13283A] mt-2">{title}</div><p className="text-[15px] text-[#5C6B73] mt-1">{d}</p></div>; })}</div>
    </Wrap>
  );
}

export function WhyFree({ t, cta }) {
  return (
    <Wrap className="bg-[#E8F8F4]" testid="challenge-why-free">
      <div className="max-w-3xl">
        <H2>{t.free.title}</H2>
        <div className="mt-4 space-y-2.5 text-[16px] text-[#36495A]">{t.free.paras.map((p, i) => <p key={p} className={i === 3 ? "font-bold text-[#13283A]" : ""}>{p}</p>)}</div>
        <div className="mt-6"><Btn onClick={() => cta("why_free")} testid="challenge-why-free-cta">{t.free.cta}</Btn></div>
      </div>
    </Wrap>
  );
}

export function Guide({ t, n, price, onBuy, onFree }) {
  const g = t.guide;
  return (
    <Wrap testid="challenge-guide-offer">
      <div className="rounded-3xl border-2 border-[#13283A] p-5 sm:p-8">
        <H2>{g.title}</H2>
        <div className="mt-2 font-display font-bold text-lg text-[#13B698]">{fill(g.sub, { n })}</div>
        <p className="text-[15px] text-[#36495A] mt-2">{fill(g.text, { n })}</p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2">{g.benefits.map((b) => <li key={b} className="flex gap-2 text-[15px] text-[#13283A]"><CheckCircle2 className="h-5 w-5 text-[#13B698] shrink-0" />{fill(b, { n })}</li>)}</ul>
        <div className="mt-5 flex items-baseline gap-2"><span className="font-display font-extrabold text-3xl text-[#13283A]" data-testid="challenge-guide-price">{price}</span><span className="text-sm text-[#5C6B73]">{g.once}</span></div>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <Btn onClick={onBuy} testid="challenge-guide-buy-btn" variant="navy">{fill(g.cta, { n, price })}</Btn>
          <Btn onClick={onFree} testid="challenge-guide-free-btn" variant="outline">{g.free}</Btn>
        </div>
        <p className="text-xs text-[#7D8C96] mt-3">{g.note}</p>
      </div>
    </Wrap>
  );
}

export function Builder({ t, price, onClick }) {
  const b = t.builder;
  return (
    <Wrap className="pt-0" testid="challenge-builder-offer">
      <div className="rounded-3xl bg-[#F4F8F9] p-5 sm:p-6">
        <h2 className="font-display font-extrabold text-xl text-[#13283A]">{b.title}</h2>
        <p className="text-[15px] text-[#36495A] mt-2">{b.text}</p>
        <div className="mt-3 font-bold text-[#13283A]" data-testid="challenge-builder-price">{fill(b.price, { price })}</div>
        <button onClick={onClick} className="mt-3 min-h-[48px] font-bold text-[#13B698] inline-flex items-center gap-1.5" data-testid="challenge-builder-btn">{b.cta} <ArrowRight className="h-4 w-4" /></button>
      </div>
    </Wrap>
  );
}

export function Faq({ t, n, price }) {
  return (
    <Wrap testid="challenge-faq">
      <div className="max-w-3xl">
        <H2>{t.faqTitle}</H2>
        <div className="mt-4 divide-y divide-[#E1ECEF] border-y border-[#E1ECEF]">
          {t.faq.map(([q, a], i) => (
            <details key={q} className="group py-3" data-testid={`challenge-faq-${i}`}>
              <summary className="flex items-center justify-between gap-3 cursor-pointer font-semibold text-[#13283A] min-h-[44px] list-none">{fill(q, { n, price })}<ChevronDown className="h-5 w-5 shrink-0 transition-transform group-open:rotate-180" /></summary>
              <p className="text-[15px] text-[#36495A] pb-2">{fill(a, { n, price })}</p>
            </details>
          ))}
        </div>
      </div>
    </Wrap>
  );
}

export function Final({ t, cta, loggedIn }) {
  return (
    <section className="bg-[#13283A] text-white" data-testid="challenge-final">
      <div className="max-w-3xl mx-auto px-5 py-12 sm:py-16 text-center">
        <PlayCircle className="h-10 w-10 text-[#13B698] mx-auto" />
        <h2 className="font-display font-extrabold text-[26px] sm:text-4xl leading-tight mt-3">{t.final.title}</h2>
        <p className="text-[16px] text-white/80 mt-3">{t.final.sub}</p>
        <div className="mt-6"><Btn onClick={() => cta("final")} testid="challenge-final-cta">{loggedIn ? t.hero.ctaLoggedIn : t.final.cta}</Btn></div>
        <p className="text-[13px] text-[#7FE3CF] font-semibold mt-2.5">{t.final.note}</p>
      </div>
    </section>
  );
}
